import React, { useEffect, useMemo, useState } from 'react';
import { LabelTextButton } from '../style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink, faRefresh } from '@fortawesome/free-solid-svg-icons';
import {
  AssignmentModel,
  AssignmentViewModel,
  TransportAssignmentModel,
} from 'api/model';
import Modal from 'components/Modal';
import {
  useAssignmentAssignRegularAssignmentToCarTransportTruckAssignment,
  useAssignmentFindAssignableAssignmentsForCarTransportTruckAssignment,
  useAssignmentGetAssignedAssignmentsForCarTransportTruckAssignment,
  useAssignmentGetUnassignedAssignmentByRegNr,
  useAssignmentUnassignRegularAssignmentFromCarTransportTruckAssignment,
} from 'api/assignment/assignment';
import { useTransportSegmentsGenerateSegmentsFromRoute } from 'api/transport-segments/transport-segments';
import styled from 'styled-components';
import { SelectOption } from 'components/inputs/SearchableSelect';
import useTranslations from 'contexts/basicData/useTranslations';
import TextButton from 'components/inputs/TextButton';
import { useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import Routes from 'constants/Routes';
import Input from 'components/inputs/Input';
import Table from 'components/Table';
import toast from 'react-hot-toast';
import { formatDayMonth } from 'utils/date-helpers';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchResultWrapper = styled(ContentWrapper)`
  max-height: 700px;
  overflow-y: auto;
`;

const MyTextButton = styled(TextButton)`
  padding: 0;
`;

interface Props {
  assignment: AssignmentModel;
  onUpdate: () => void;
}

const AssignCarsToCarTransportTruckModal: React.FC<Props> = ({
  assignment,
  onUpdate,
}) => {
  const queryClient = useQueryClient();
  const { caseTypes } = useTranslations();

  const currentCarTransportTruckAssignmentsForAssignmentCall =
    useAssignmentGetAssignedAssignmentsForCarTransportTruckAssignment(
      {
        assignmentId: assignment.assignmentID,
      },
      {
        query: {
          enabled: assignment.assignmentID !== -1,
        },
      }
    );
  const assignRegularAssignmentToCarTransportTruckAssignmentCall =
    useAssignmentAssignRegularAssignmentToCarTransportTruckAssignment();
  const unassignAssignRegularAssignmentToCarTransportTruckAssignmentCall =
    useAssignmentUnassignRegularAssignmentFromCarTransportTruckAssignment();

  const getUnassignedAssignmentByRegNrCall =
    useAssignmentGetUnassignedAssignmentByRegNr();

  const generateSegmentsFromRouteCall =
    useTransportSegmentsGenerateSegmentsFromRoute();

  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState<string>('');
  const findAssignableAssignmentsForCarTransportTruckAssignmentCall =
    useAssignmentFindAssignableAssignmentsForCarTransportTruckAssignment();

  const refreshData = async () => {
    if (assignment && assignment.assignmentID !== -1) {
      await findAssignableAssignmentsForCarTransportTruckAssignmentCall.mutateAsync(
        {
          params: {
            fromAddress: assignment.fromAddress,
            fromZip: assignment.fromZip,
            toAddress: assignment.toAddress,
            toZip: assignment.toZip,
          },
        }
      );
      await queryClient.invalidateQueries(
        currentCarTransportTruckAssignmentsForAssignmentCall.queryKey
      );
      getUnassignedAssignmentByRegNrCall.reset();

      await currentCarTransportTruckAssignmentsForAssignmentCall.refetch();
    }
  };

  useEffect(() => {
    if (showModal) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const searchableAssignments = useMemo(() => {
    const assignments: AssignmentViewModel[] = [
      ...(findAssignableAssignmentsForCarTransportTruckAssignmentCall.data ??
        []),
    ];
    if (getUnassignedAssignmentByRegNrCall.data) {
      assignments.push(getUnassignedAssignmentByRegNrCall.data);
    }

    const options: SelectOption<number | null, AssignmentViewModel | null>[] =
      assignments.map((a) => ({
        label: a.case.registrationNumber,
        value: a.assignment.assignmentID,
        extra: a,
      }));

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    findAssignableAssignmentsForCarTransportTruckAssignmentCall.data,
    getUnassignedAssignmentByRegNrCall.data,
    search,
  ]);

  const searchedAssignments = useMemo(() => {
    return searchableAssignments.filter((a) =>
      a.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, searchableAssignments]);

  const assignAssignment = async (value: number | null) => {
    if (
      value &&
      !assignRegularAssignmentToCarTransportTruckAssignmentCall.isLoading
    ) {
      await assignRegularAssignmentToCarTransportTruckAssignmentCall.mutateAsync(
        {
          params: {
            assignmentId: value,
            carTransportTruckAssignmentId: assignment.assignmentID,
          },
        }
      );

      // 2. Refresh data to get updated car list
      await refreshData();

      // 3. Automatically (re)generate segments from all assigned cars
      try {
        // Refetch again to ensure we have the absolute latest data including the car we just added
        const { data: freshAssignedCars } =
          await currentCarTransportTruckAssignmentsForAssignmentCall.refetch();
        const assignedCars = freshAssignedCars ?? [];

        if (assignedCars.length > 0) {
          // Always regenerate segments to include the newly added car
          const segments = await generateSegmentsFromRouteCall.mutateAsync({
            params: {
              truckAssignmentId: assignment.assignmentID,
            },
            data: assignedCars,
          });

          if (segments && segments.length > 0) {
            toast.success(
              `${segments.length} segment${segments.length > 1 ? '' : ''} ${assignedCars.length === 1 ? 'skapades' : 'uppdaterades'} automatiskt`,
              { duration: 3000 }
            );
            // Refresh again to show the new segments
            await refreshData();
          }
        }
      } catch (segmentError) {
        // Don't fail the whole operation if segment generation fails
        // console.error('Failed to generate segments:', segmentError);
        toast.error(
          'Bilen tilldelades men segment kunde inte skapas automatiskt',
          { duration: 5000 }
        );
      }
    }
  };

  const unassignAssignment = async (a: TransportAssignmentModel) => {
    await unassignAssignRegularAssignmentToCarTransportTruckAssignmentCall.mutateAsync(
      {
        params: {
          assignmentId: a.loadedAssignmentID,
          carTransportTruckAssignmentId: assignment.assignmentID,
        },
      }
    );
    refreshData();
  };

  return (
    <>
      <LabelTextButton onClick={() => setShowModal(true)}>
        Lägg till/ta bort
      </LabelTextButton>
      {showModal && (
        <Modal
          title="Hantera bilar"
          onClose={() => {
            onUpdate();
            setShowModal(false);
          }}
        >
          <ContentWrapper>
            {currentCarTransportTruckAssignmentsForAssignmentCall.isLoading ||
            assignRegularAssignmentToCarTransportTruckAssignmentCall.isLoading ||
            unassignAssignRegularAssignmentToCarTransportTruckAssignmentCall.isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <strong>Tilldelade bilar</strong>
                {(
                  currentCarTransportTruckAssignmentsForAssignmentCall.data ??
                  []
                )?.length === 0 && <span>Inga tilldelade bilar</span>}
                <Table
                  columnSettings={[
                    {
                      head: 'Registreringsnummer',
                      render: (option) => (
                        <MyTextButton
                          onClick={() => {
                            window.open(
                              `${Routes.search.index}/${option.loadedAssignmentCaseID}`,
                              option.loadedAssignmentCaseID
                                ? String(option.loadedAssignmentCaseID)
                                : '_blank'
                            );
                          }}
                        >
                          {option.caseRegistrationNumber}{' '}
                          <FontAwesomeIcon icon={faExternalLink} />
                        </MyTextButton>
                      ),
                      width: 150,
                    },
                    {
                      head: 'Skapat',
                      render: (option) =>
                        option.caseCreated
                          ? formatDayMonth(option.caseCreated)
                          : '–',
                      width: 100,
                    },
                    {
                      head: 'Ärendetyp',
                      render: (option) =>
                        option.caseTypeID && caseTypes[option.caseTypeID!],
                      width: 100,
                    },
                    {
                      head: 'Företag',
                      render: (option) => option.companyName ?? 'Inget företag',
                      width: 200,
                    },
                    {
                      head: ' ',
                      render: (option) => (
                        <MyTextButton
                          onClick={() => unassignAssignment(option)}
                        >
                          Ta bort
                        </MyTextButton>
                      ),
                      width: 100,
                    },
                  ]}
                  rows={
                    currentCarTransportTruckAssignmentsForAssignmentCall.data ??
                    []
                  }
                />
                <strong>Tillgängliga bilar</strong>
                <Input
                  placeholder="Sök på registreringsnummer"
                  value={search ?? ''}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    getUnassignedAssignmentByRegNrCall.reset();
                  }}
                />
                <SearchResultWrapper>
                  <div>
                    {searchableAssignments.length === 0 && (
                      <span>Inga bilar matchar addressen</span>
                    )}
                    {searchedAssignments.length === 0 && search.length > 0 && (
                      <TextButton
                        onClick={async () => {
                          await getUnassignedAssignmentByRegNrCall.mutateAsync({
                            params: {
                              regNr: search,
                            },
                          });
                        }}
                      >
                        Sök på endast regnr{' '}
                        <FontAwesomeIcon
                          icon={faRefresh}
                          spin={getUnassignedAssignmentByRegNrCall.isLoading}
                        />
                      </TextButton>
                    )}
                  </div>
                  <Table
                    columnSettings={[
                      {
                        head: 'Registreringsnummer',
                        render: (option) => (
                          <MyTextButton
                            onClick={() => {
                              window.open(
                                `${Routes.search.index}/${option.extra?.case.caseID}`,
                                option.extra?.case.caseID
                                  ? String(option.extra?.case.caseID ?? '')
                                  : '_blank'
                              );
                            }}
                          >
                            {option.extra?.case.registrationNumber}{' '}
                            <FontAwesomeIcon icon={faExternalLink} />
                          </MyTextButton>
                        ),
                        width: 150,
                      },
                      {
                        head: 'Skapat',
                        render: (option) =>
                          option.extra?.case.created
                            ? formatDayMonth(option.extra.case.created)
                            : '–',
                        width: 100,
                      },
                      {
                        head: 'Ärendetyp',
                        render: (option) =>
                          option.extra &&
                          caseTypes[option.extra.case.caseTypeID!],
                        width: 100,
                      },
                      {
                        head: 'Företag',
                        render: (option) =>
                          option.extra?.case.companyName ?? 'Inget företag',
                        width: 200,
                      },
                      {
                        head: ' ',
                        render: (option) => (
                          <MyTextButton
                            onClick={() => assignAssignment(option.value)}
                          >
                            Lägg till
                          </MyTextButton>
                        ),
                        width: 100,
                      },
                    ]}
                    rows={searchedAssignments}
                  />
                </SearchResultWrapper>
              </>
            )}
          </ContentWrapper>
        </Modal>
      )}
    </>
  );
};

export default AssignCarsToCarTransportTruckModal;
